"use client";
/**
 * useMountTransition — CSS transition için mount/unmount yönetimi.
 *
 * Kullanım:
 *   const { rendered, isVisible } = useMountTransition(isOpen, 300);
 *   if (!rendered) return null;
 *   <div style={{ transform: isVisible ? "translateY(0)" : "translateY(100%)",
 *                 transition: "transform 300ms ease-out" }} />
 *
 * SORUN & ÇÖZÜM:
 *   React 18 batching: rendered zaten true iken setIsVisible(false) + 2rAF + setIsVisible(true)
 *   aynı commit'e sıkışabilir → "kapalı" frame hiç paint edilmez → animasyonsuz açılış.
 *
 *   ÇÖZÜM: Her açılışta rendered=false (unmount) → setTimeout(0) → rendered=true (yeni mount)
 *   → rAF → isVisible=true. Yeni DOM node garantisi → CSS transition her zaman çalışır.
 *
 *   setTimeout(0): macrotask kuyruğuna atar → React batch dışı → ayrı commit garantisi.
 */
import { useState, useEffect, useRef } from "react";

export function useMountTransition(active: boolean, durationMs = 300) {
  const [rendered, setRendered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    // Önceki pending işlemleri iptal et
    cancelAnimationFrame(rafRef.current);
    if (openTimerRef.current !== null) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }

    if (active) {
      // Kapanış timer'ını iptal et
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      // Adım 1: Önce unmount et (isVisible da false olsun)
      setIsVisible(false);
      setRendered(false);

      // Adım 2: Macrotask sonrası yeniden mount et → garantili yeni DOM node
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        setRendered(true);
        // Adım 3: React commit → paint → rAF → isVisible=true → CSS transition
        rafRef.current = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      }, 0);
    } else {
      // Kapanış: CSS transition başlat, sonra DOM'dan kaldır
      setIsVisible(false);
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
      }
      closeTimerRef.current = setTimeout(() => {
        setRendered(false);
        closeTimerRef.current = null;
      }, durationMs);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (openTimerRef.current !== null) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [active, durationMs]); // eslint-disable-line react-hooks/exhaustive-deps

  return { rendered, isVisible };
}